/**
 * Risk assessment checklist definitions.
 *
 * Transitional: hardcoded from the paper "Updated Risk Assessment Form
 * 040221_Fillable.pdf". These should migrate to inspection sub-type
 * `configuration_data` (per-sub-type safety/wellness checklists) so tenants
 * can author their own checklist via the Entity Editor. They live in their
 * own module (not the modal component file) so both the capture modal and
 * the read-only results summary can import them without tripping the
 * react-refresh "only export components" rule.
 */

export type SafetyGroup = 'general' | 'ladder'

export interface SafetyQuestion {
  id: number
  text: string
  group: SafetyGroup
}

export const SAFETY_QUESTIONS: SafetyQuestion[] = [
  {
    id: 1,
    text: 'Employee has completed formal Roof Access Training and is certified in Fall Protection Procedures.',
    group: 'general',
  },
  {
    id: 2,
    text: 'Employee is wearing appropriate PPE (including roof-rated footwear such as Cougar Paws).',
    group: 'general',
  },
  {
    id: 3,
    text: 'Weather conditions are acceptable (low/no wind, no rain, ice or snow).',
    group: 'general',
  },
  {
    id: 4,
    text: 'Roof cannot be inspected from ground, ladder, or drone - must access.',
    group: 'general',
  },
  { id: 5, text: 'Roof top condition is not slippery.', group: 'general' },
  {
    id: 6,
    text: 'Will not inspect within 6 feet of roof edge unless fall protection is used or exception applies.',
    group: 'general',
  },
  {
    id: 7,
    text: 'Slope is not greater than 7/12 - if greater, fall protection MUST be used.',
    group: 'general',
  },
  {
    id: 8,
    text: 'Will avoid being within 6 feet of skylights/windows/holes unless fall protection or exception applies.',
    group: 'general',
  },
  { id: 9, text: 'Area has been inspected for trip hazards.', group: 'general' },
  { id: 10, text: 'Flashlights available for low-light access.', group: 'general' },
  {
    id: 11,
    text: 'Only approved material-lifting devices used to bring equipment to the roof.',
    group: 'general',
  },
  {
    id: 12,
    text: 'A "Spotter" is on-site and available to call 911 if needed.',
    group: 'general',
  },
  {
    id: 13,
    text: 'If using a MEWP (Mobile Elevating Work Platform), tied off to the platform.',
    group: 'general',
  },
  {
    id: 14,
    text: 'MEWP operator is an authorised/trained operator and has briefed on emergencies.',
    group: 'general',
  },
  {
    id: 15,
    text: 'If in MEWP near overhead projections or powerlines, all occupants wear a hard hat.',
    group: 'general',
  },
  { id: 16, text: 'Ladder(s) are in good working condition.', group: 'general' },
  {
    id: 17,
    text: 'Ladder is properly secured at the top or secured at base by another worker.',
    group: 'ladder',
  },
  {
    id: 18,
    text: 'Space available for proper ladder set-up (4:1 ratio).',
    group: 'ladder',
  },
  {
    id: 19,
    text: 'Ladder extends three feet above the level being accessed.',
    group: 'ladder',
  },
  {
    id: 20,
    text: 'Before exiting the ladder to the roof, checked for roof rot.',
    group: 'ladder',
  },
]

export const SAFETY_GROUPS: { key: SafetyGroup; label: string }[] = [
  { key: 'general', label: 'General roof safety' },
  { key: 'ladder', label: 'Ladder access' },
]

export interface WellnessQuestion {
  key: string
  text: string
}

export const WELLNESS: WellnessQuestion[] = [
  { key: 'dizziness', text: 'Dizzy or had dizziness within the last month?' },
  {
    key: 'medication',
    text: 'Taken any medication in the past 12hrs that may cause drowsiness or warns against operating heavy machinery?',
  },
  {
    key: 'pain',
    text: 'Currently experiencing pain in back, knees, shoulders, or hands that may impact safe climbing?',
  },
  { key: 'illness', text: 'Feeling ill or fatigued?' },
  {
    key: 'other',
    text: 'Any other issues that might create a risk when inspecting?',
  },
]

export type AnswerValue = 'yes' | 'no' | 'na' | ''
